import { apiRequest, apiRoutes } from '../../api';
import { buildMediaUrl } from '../../config/appConfig';
import React, { useState, useEffect, useCallback } from 'react';
import './PropertyWizard.css';

// Photo Step Component
function PhotoStep({ onSubmit, initialData = {}, onPreviousStep, propertyId }) {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [captions, setCaptions] = useState({});

  // Load existing photos when component mounts
  useEffect(() => {

    if (propertyId) {
      loadPhotos();
    } else {
      setLoading(false);
    }
  }, [propertyId]);

  const loadPhotos = async ({ showLoading = true } = {}) => {
    try {
      if (showLoading) setLoading(true);
      const response = await apiRequest(apiRoutes.properties.images(propertyId), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPhotos(data);
      } else {

      }
    } catch (err) {

      setError('خطا در بارگذاری عکس‌ها');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const uploadPhoto = async (file) => {
    // Validate propertyId
    if (!propertyId) {
      throw new Error('Property ID is required for uploading images');
    }

    const formData = new FormData();

    formData.append('image', file);

    // Use custom caption if available, otherwise fall back to filename
    const caption = captions[file.name] || file.name;
    formData.append('caption', caption);

    formData.append('alt_text', `عکس ملک - ${file.name}`);

    // Send property as the main identifier
    formData.append('property', propertyId);

    try {
      const response = await apiRequest(apiRoutes.properties.images(propertyId), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: formData
      });

      if (response.ok) {
        const newPhoto = await response.json();
        setPhotos(prev => [...prev, newPhoto]);
        return newPhoto;
      } else {
        let errorMessage = 'خطا در آپلود عکس';
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const errorData = await response.json();

          if (errorData.detail) {
            errorMessage = errorData.detail;
          } else if (errorData.non_field_errors?.length > 0) {
            errorMessage = errorData.non_field_errors[0];
          } else if (errorData.image?.length > 0) {
            errorMessage = `خطا در فایل: ${errorData.image[0]}`;
          } else if (errorData.property?.length > 0) {
            errorMessage = `خطا در شناسه ملک: ${errorData.property[0]}`;
          } else if (errorData.caption?.length > 0) {
            errorMessage = `خطا در توضیحات: ${errorData.caption[0]}`;
          } else if (typeof errorData === 'string') {
            errorMessage = errorData;
          }
        } else {

        }
        throw new Error(errorMessage);
      }
    } catch (err) {

      throw err;
    }
  };

  const handleFileSelect = (files) => {
    const fileArray = Array.from(files);
    setSelectedFiles(fileArray);

    // Initialize captions for new files
    const newCaptions = { ...captions };
    fileArray.forEach(file => {
      if (!newCaptions[file.name]) {
        newCaptions[file.name] = file.name; // Default to filename
      }
    });
    setCaptions(newCaptions);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setUploading(true);
    setError(null);

    try {
      const uploadPromises = selectedFiles.map(file => uploadPhoto(file));
      await Promise.all(uploadPromises);
      await loadPhotos({ showLoading: false });

      // Clear selected files and captions after successful upload
      setSelectedFiles([]);
      setCaptions({});
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleCaptionChange = (fileName, caption) => {
    setCaptions(prev => ({
      ...prev,
      [fileName]: caption
    }));
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
  }, []);

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files);
    }
  };

  const deletePhoto = async (photoId) => {
    try {
      const response = await apiRequest(apiRoutes.properties.image(propertyId, photoId), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        setPhotos(prev => prev.filter(photo => photo.id !== photoId));
      } else {
        setError('خطا در حذف عکس');
      }
    } catch (err) {

      setError('خطا در حذف عکس');
    }
  };

  const setPrimaryPhoto = async (photoId) => {
    try {
      const response = await apiRequest(apiRoutes.properties.updatePrimaryImage(propertyId, photoId), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ make_primary: true })
      });

      if (response.ok) {
        // Update local state to reflect the change
        setPhotos(prev => prev.map(photo => ({
          ...photo,
          is_primary: photo.id === photoId
        })));
      } else {
        setError('خطا در تنظیم عکس اصلی');
      }
    } catch (err) {

      setError('خطا در تنظیم عکس اصلی');
    }
  };

  const reorderPhotos = async (newOrder) => {
    try {
      const items = newOrder.map((photo, index) => ({
        id: photo.id,
        sort_order: index
      }));

      const response = await apiRequest(apiRoutes.properties.reorderImages(propertyId), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ items })
      });

      if (response.ok) {
        const reorderedPhotos = await response.json();
        setPhotos(reorderedPhotos);
      } else {
        setError('خطا در مرتب‌سازی عکس‌ها');
      }
    } catch (err) {

      setError('خطا در مرتب‌سازی عکس‌ها');
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(photos);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setPhotos(items);
    reorderPhotos(items);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      photos: photos.map(photo => photo.id),
      completed: true
    });
  };

  if (loading) {
    return (
      <div className="photo-step">
        <div className="step-header">
          <h3>آپلود عکس‌ها</h3>
          <p>در حال بارگذاری عکس‌های موجود...</p>
        </div>
        <div className="loading-overlay">
          <div className="loading-text">در حال بارگذاری...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="photo-step-compact">
      {/* Compact Header */}
      <div className="compact-header">
        <h3>آپلود عکس‌ها</h3>
        {photos.length > 0 && <span className="photo-count">{photos.length} عکس</span>}
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-message-compact">
          {error}
        </div>
      )}

      {/* Main Content Area - Two Columns */}
      <div className="photo-content-grid">
        {/* Left Column: Upload Area */}
        <div className="upload-column">
          <div 
            className={`upload-area-compact ${dragActive ? 'drag-active' : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => !uploading && document.querySelector('.file-input').click()}
          >
            <div
              className="upload-text-compact"
              style={{ color: 'var(--saita-color-text-primary, #1f2937)' }}
            >
              {dragActive ? 'رها کنید' : 'کلیک یا بکشید'}
            </div>
            <div className="upload-hint-compact">JPG, PNG, WebP</div>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileInputChange}
              className="file-input"
              disabled={uploading}
            />
          </div>

          {/* Selected Files Preview */}
          {selectedFiles.length > 0 && (
            <div className="selected-files-compact">
              <div className="selected-header">
                <span>{selectedFiles.length} فایل انتخاب شده</span>
                <button
                  type="button"
                  onClick={handleUpload}
                  className="upload-btn-compact"
                  disabled={uploading}
                >
                  {uploading ? 'در حال آپلود...' : 'آپلود'}
                </button>
              </div>
              <div className="selected-list">
                {selectedFiles.map((file) => (
                  <div key={file.name} className="selected-item">
                    <img 
                      src={URL.createObjectURL(file)} 
                      alt={file.name}
                      className="selected-thumb"
                    />
                    <input
                      type="text"
                      value={captions[file.name] || ''}
                      onChange={(e) => handleCaptionChange(file.name, e.target.value)}
                      placeholder="توضیح..."
                      className="caption-input-compact"
                      disabled={uploading}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Progress */}
          {uploading && (
            <div className="upload-progress-compact">
              <div className="progress-bar-compact">
                <div className="progress-fill-compact"></div>
              </div>
              <span className="progress-text-compact">در حال آپلود...</span>
            </div>
          )}
        </div>

        {/* Right Column: Uploaded Photos Grid */}
        <div className="photos-column">
          {photos.length === 0 ? (
            <div className="no-photos-message">
              <p>هنوز عکسی آپلود نشده</p>
              <p className="hint-text">حداقل یک عکس برای ادامه نیاز است</p>
            </div>
          ) : (
            <div className="photos-grid-compact">
              {photos.map((photo, index) => (
                <div key={photo.id} className="photo-card-compact">
                  <div className="photo-image-wrapper">
                    <img 
                      src={buildMediaUrl(photo.read_url || photo.image || photo.urls?.thumb || photo.urls?.sm)}
                      alt={photo.alt_text || photo.caption}
                      className="photo-img-compact"
                    />
                    {photo.is_primary && (
                      <div className="primary-badge-compact">عکس اصلی</div>
                    )}
                    <div className="photo-actions-overlay">
                      <button
                        type="button"
                        className="action-btn-compact primary-btn"
                        onClick={() => setPrimaryPhoto(photo.id)}
                        disabled={photo.is_primary}
                        title="عکس اصلی"
                      >
                        اصلی
                      </button>
                      <button
                        type="button"
                        className="action-btn-compact delete-btn"
                        onClick={() => deletePhoto(photo.id)}
                        title="حذف"
                      >
                        حذف
                      </button>
                    </div>
                  </div>
                  <div className="photo-caption-compact">
                    {photo.caption || `عکس ${index + 1}`}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="bottom-nav-compact">
        <button 
          type="button"
          onClick={handleSubmit}
          className="nav-btn-compact next-btn"
          disabled={uploading || selectedFiles.length > 0 || photos.length === 0}
        >
          {uploading ? 'لطفاً صبر کنید...' : 'تأیید و ادامه'}
        </button>
        <button 
          type="button"
          onClick={onPreviousStep}
          className="nav-btn-compact prev-btn"
          disabled={uploading}
        >
          بازگشت
        </button>
      </div>
    </div>
  );
}

export default PhotoStep;
